/** What a person may attach to one chat message: the figures both the composer and the server read. */
export type ChatFileLimits = {
    /** The most bytes one file may hold, inclusive. */
    maxFileBytes: number
    /** The most files one message may carry, inclusive. */
    maxFilesPerMessage: number
}

const KB = 1024
const MB = 1024 * KB

/**
 * The one published contract for chat file limits (#1731): 10 MB per file, 5 files per message, both inclusive.
 * A megabyte is 1024 × 1024 bytes. The composer states these before anything is chosen and the API refuses
 * against them, so neither holds a figure of its own and the two cannot drift.
 */
export const CHAT_FILE_LIMITS: ChatFileLimits = { maxFileBytes: 10 * MB, maxFilesPerMessage: 5 }

/** A whole number as written, a fraction to one place: `10`, `1.5`. */
function figure(value: number): string {
    return Number.isInteger(value) ? String(value) : value.toFixed(1).replace(/\.0$/, '')
}

/** A byte count as a person reads it: `10 MB`, `512 KB`, `100 bytes`, in the 1024-based units the limits use. */
export function formatChatFileSize(bytes: number): string {
    if (bytes >= MB) {
        return `${figure(bytes / MB)} MB`
    }
    if (bytes >= KB) {
        return `${figure(bytes / KB)} KB`
    }
    return bytes === 1 ? '1 byte' : `${bytes} bytes`
}

/** The per-message limit as a phrase: `5 files per message`, `1 file per message`. */
export function chatFilesPerMessage(count: number): string {
    return `${count} ${count === 1 ? 'file' : 'files'} per message`
}

/** The limits as one sentence a person reads before uploading: `10 MB per file, 5 files per message`. */
export function chatFileLimitsSentence(limits: ChatFileLimits = CHAT_FILE_LIMITS): string {
    return `${formatChatFileSize(limits.maxFileBytes)} per file, ${chatFilesPerMessage(limits.maxFilesPerMessage)}`
}

/** The kinds of file a chat message may carry (#1732), judged by filename extension. */
export type ChatFileKind = 'png' | 'jpg' | 'pdf' | 'text' | 'markdown' | 'csv' | 'word' | 'excel'

/**
 * The one supported-type contract for chat files (#1732): each extension a message may carry, lower-case, and the
 * kind it names. The server refuses against it and the refusal sentence below is written for it, so neither holds a
 * list of its own. A Map, not an object, so a name ending `.constructor` or `.toString` names no kind.
 */
const KIND_BY_EXTENSION: ReadonlyMap<string, ChatFileKind> = new Map<string, ChatFileKind>([
    ['png', 'png'],
    ['jpg', 'jpg'],
    ['jpeg', 'jpg'],
    ['pdf', 'pdf'],
    ['txt', 'text'],
    ['md', 'markdown'],
    ['markdown', 'markdown'],
    ['csv', 'csv'],
    ['doc', 'word'],
    ['docx', 'word'],
    ['xls', 'excel'],
    ['xlsx', 'excel'],
])

/**
 * The supported kind a filename's extension names, case-insensitively; null for any other. Only the last extension
 * counts, so `report.pdf.exe` is an `.exe` and `installer.exe.pdf` is a PDF; a name with no extension names no kind.
 * The name is the whole judgement of what a message may carry. It is not what an image is: the turn that shows one
 * decides its media type from its bytes (`chat-attachments.ts` in the API, #1733).
 */
export function chatFileKind(filename: string): ChatFileKind | null {
    const dot = filename.lastIndexOf('.')
    if (dot < 0) {
        return null
    }
    return KIND_BY_EXTENSION.get(filename.slice(dot + 1).toLowerCase()) ?? null
}

/** Whether a chat message may carry a file of this name. */
export function isSupportedChatFile(filename: string): boolean {
    return chatFileKind(filename) !== null
}

/** The supported kinds as one sentence a person reads in a refusal. */
export function supportedChatFileKindsSentence(): string {
    return 'You can attach PNG and JPG images, PDF, text and Markdown, CSV, and Word and Excel documents.'
}
